import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Youtube } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { LOGO_URL } from "@/lib/brand";

export default function Footer() {
  const { settings } = useSettings();
  const s = settings || {};

  return (
    <footer data-testid="site-footer" className="relative bg-[#2d1616] text-[#fdfbf7]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand + contact */}
          <div className="lg:col-span-4">
            <span className="inline-flex bg-white rounded-xl p-3 shadow-lg">
              <img src={LOGO_URL} alt="Legacy India Travel" data-testid="footer-logo" className="h-28 w-auto object-contain" />
            </span>
            <p className="mt-6 text-[#fdfbf7]/70 leading-relaxed max-w-sm">
              Discover India, Experience Extraordinary.
            </p>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`tel:${(s.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-3 text-[#fdfbf7]/85 hover:text-[#e8c970] transition-colors">
                <Phone className="w-4 h-4 text-[#d4af37]" /> {s.phone} {s.phone_alt ? `/ ${s.phone_alt}` : ""}
              </a>
              <a href={`mailto:${s.email}`} className="flex items-center gap-3 text-[#fdfbf7]/85 hover:text-[#e8c970] transition-colors">
                <Mail className="w-4 h-4 text-[#d4af37]" /> {s.email}
              </a>
              <div className="flex items-start gap-3 text-[#fdfbf7]/85">
                <MapPin className="w-4 h-4 text-[#d4af37] mt-0.5" /> <span>{s.address}</span>
              </div>
              <div className="flex items-start gap-3 text-[#fdfbf7]/85">
                <Clock className="w-4 h-4 text-[#d4af37] mt-0.5" /> <span>{s.office_hours}</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              {s.social?.instagram && <a href={s.social.instagram} aria-label="Instagram" className="w-10 h-10 border border-[#d4af37]/40 grid place-items-center hover:bg-[#d4af37] hover:text-[#2d1616] transition-colors"><Instagram className="w-4 h-4" /></a>}
              {s.social?.facebook && <a href={s.social.facebook} aria-label="Facebook" className="w-10 h-10 border border-[#d4af37]/40 grid place-items-center hover:bg-[#d4af37] hover:text-[#2d1616] transition-colors"><Facebook className="w-4 h-4" /></a>}
              {s.social?.youtube && <a href={s.social.youtube} aria-label="Youtube" className="w-10 h-10 border border-[#d4af37]/40 grid place-items-center hover:bg-[#d4af37] hover:text-[#2d1616] transition-colors"><Youtube className="w-4 h-4" /></a>}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FooterCol title="Explore" links={[["Tours", "/tours"], ["Destinations", "/destinations"], ["About India", "/about-india"], ["Blog", "/blog"]]} />
            <FooterCol title="Popular Tours" links={[["Golden Triangle", "/tours?category=Golden%20Triangle"], ["Kashmir", "/tours?category=Kashmir%20Tours"], ["Kerala", "/tours?category=Kerala%20Tours"], ["Rajasthan", "/tours?category=Rajasthan%20Tours"]]} />
            <FooterCol title="Company" links={[["Reviews", "/testimonials"], ["Contact", "/contact"], ["FAQ", "/faq"], ["Enquire", "/contact"]]} />
            <FooterCol title="Legal" links={[["Privacy Policy", "/privacy-policy"], ["Terms", "/terms"], ["Cancellation", "/cancellation-policy"], ["Cookie Policy", "/cookie-policy"]]} />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#d4af37]/20 flex flex-col md:flex-row gap-3 md:gap-6 md:items-center text-xs text-[#fdfbf7]/60">
          <div className="flex items-center gap-4 flex-wrap">
            <span>© {new Date().getFullYear()} {s.company_name || "Legacy India Travel"}. All rights reserved.</span>
            <span className="text-[#d4af37]/40 hidden md:inline">·</span>
            <Link
              to="/cms/login"
              data-testid="footer-admin-link"
              className="text-[#e8c970]/80 hover:text-[#e8c970] tracking-[0.22em] uppercase text-[10px] transition-colors"
            >
              Admin
            </Link>
          </div>
          <div className="md:ml-auto md:pr-24">Curated India journeys · Trusted by travellers worldwide</div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.26em] uppercase text-[#e8c970] mb-5">{title}</div>
      <ul className="space-y-3">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link to={to} className="text-sm text-[#fdfbf7]/80 hover:text-[#e8c970] transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
